import { generateImagePath } from '@worldcoo/shared/utils';

declare global {
	interface Window {
		__CDN_URL?: string;
		__IMGPROXY_URL?: string;
		__ANALYTICS_CODE?: string;
		__GOOGLE_RECAPTCHA_SITE_KEY?: string;
		__ADYEN_CLIENT_KEY?: string;
		__USERCENTRICS_SETTINGS_ID?: string;
		__USERCENTRICS_DRAFT?: string;
	}
}

// Resolves a deployment-time env var at request/render time. Server reads from
// process.env (the live env), client reads from a window stash populated by the
// SSR-rendered inline script in __root.tsx. This lets a single prod artifact
// run against any environment without a rebuild and keeps SSR/hydration markup
// byte-identical (no React re-renders or hydration churn).
function readRuntimeEnv(windowKey: keyof Window, processKey: string): string {
	if (typeof window !== 'undefined')
		return (window as unknown as Record<string, string | undefined>)[windowKey as string] ?? '';
	if (typeof process !== 'undefined') return process.env[processKey] ?? '';
	return '';
}

export const getRuntimeCdnUrl = (): string => readRuntimeEnv('__CDN_URL', 'VITE_CDN_URL');
export const getRuntimeImgproxyUrl = (): string => readRuntimeEnv('__IMGPROXY_URL', 'VITE_IMGPROXY_URL');
export const getRuntimeAnalyticsCode = (): string => readRuntimeEnv('__ANALYTICS_CODE', 'VITE_ANALYTICS_CODE');
export const getRuntimeRecaptchaSiteKey = (): string =>
	readRuntimeEnv('__GOOGLE_RECAPTCHA_SITE_KEY', 'VITE_GOOGLE_RECAPTCHA_SITE_KEY');
export const getRuntimeAdyenClientKey = (): string => readRuntimeEnv('__ADYEN_CLIENT_KEY', 'VITE_ADYEN_CLIENT_KEY');
export const getRuntimeUsercentricsSettingsId = (): string =>
	readRuntimeEnv('__USERCENTRICS_SETTINGS_ID', 'VITE_USERCENTRICS_SETTINGS_ID');
/** `'true'` on every stage but `pro` — see the `data-draft` note in `loadUsercentrics`. */
export const getRuntimeUsercentricsDraft = (): string =>
	readRuntimeEnv('__USERCENTRICS_DRAFT', 'VITE_USERCENTRICS_DRAFT');

export function generateImageSrc(
	src: string,
	opts?: { height?: number; width?: number; format?: 'webp' | 'avif'; quality?: number; resizeType?: 'fill' | 'fit' },
): string {
	const imgproxyUrl = getRuntimeImgproxyUrl();
	if (!imgproxyUrl) return src;

	return imgproxyUrl + generateImagePath(src, opts);
}

export function buildPortalUrl(portalId: string): string {
	if (typeof window === 'undefined') return '';
	const { protocol, hostname, port } = window.location;
	const parts = hostname.split('.');
	parts[0] = portalId;
	const portSuffix = port ? `:${port}` : '';
	return `${protocol}//${parts.join('.')}${portSuffix}`;
}

export function getPortalDomainSuffix(): string {
	if (typeof window === 'undefined') return '';
	const { hostname, port } = window.location;
	const parts = hostname.split('.');
	const portSuffix = port ? `:${port}` : '';
	return `.${parts.slice(1).join('.')}${portSuffix}`;
}

export const getLogoImageSize = (portalId: string) => ({
	height:
		portalId === 'awwg'
			? 72
			: portalId === 'generaloptica'
				? 72
				: portalId === 'maxsupermercati'
					? 72
					: portalId === 'eroski'
						? 60
						: portalId === 'familasicilia'
							? 60
							: portalId === 'fundacionrealmadrid'
								? 60
								: portalId === 'portaventura'
									? 60
									: 50,
});
